<template>
    <div class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm h-full flex flex-col">
        <div class="flex items-start justify-between">
            <div>
                <div
                    class="inline-flex items-center px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs font-semibold">
                    💡 Think Tank
                </div>
                <h3 class="mt-4 text-2xl font-extrabold text-indigo-900">
                    Think Tanks
                    <span v-if="totalLikes > 0 || totalComments > 0" class="text-lg font-semibold text-gray-600 ml-2">
                        (<span class="text-indigo-700">{{ totalLikes }}</span> likes, <span class="text-gray-700">{{
                            totalComments }}</span> comments)
                    </span>
                </h3>
                <p class="mt-2 text-gray-600">
                    Creative ideas shared by {{ userName || 'this user' }} to enhance institutional growth and
                    collaboration.
                </p>
            </div>
        </div>

        <div v-if="isLoading && items.length === 0" class="flex-1 grid place-items-center">
            <div class="flex items-center space-x-3 text-gray-500">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span class="text-sm">Loading Think Tanks...</span>
            </div>
        </div>
        <div v-else-if="items.length === 0" class="flex-1 grid place-items-center">
            <div class="text-center">
                <div
                    class="w-24 h-24 mx-auto rounded-full bg-indigo-50 border border-indigo-100 grid place-items-center">
                    <font-awesome-icon icon="lightbulb" class="text-3xl text-indigo-600" />
                </div>
                <h3 class="mt-5 text-lg font-semibold text-indigo-900">No Think Tanks yet</h3>
                <p class="mt-1 text-sm text-gray-600">Ideas shared by {{ userName || 'this user' }} will appear here.
                </p>
                <div class="mt-4">
                    <button @click="openThinkTank"
                        class="px-4 py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm transition">Share
                        Your Idea Now</button>
                </div>
            </div>
        </div>
        <div v-else class="mt-6 flex flex-col">
            <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div class="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p class="text-sm text-gray-500">Total Ideas</p>
                    <p class="mt-1 text-3xl font-extrabold text-indigo-900">{{ metricIdeas }}</p>
                </div>
                <div class="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p class="text-sm text-gray-500">Total Likes</p>
                    <p class="mt-1 text-3xl font-extrabold text-indigo-900">{{ totalLikes }}</p>
                </div>
                <div class="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p class="text-sm text-gray-500">Comments</p>
                    <p class="mt-1 text-3xl font-extrabold text-indigo-900">{{ totalComments }}</p>
                </div>
            </div>
            <div class="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div v-for="item in items" :key="item.token"
                    class="rounded-xl border border-gray-200 bg-white hover:shadow-md transition cursor-pointer flex flex-col overflow-hidden">
                    <div class="h-32 bg-indigo-50 border-b border-gray-100 grid place-items-center">
                        <span class="text-4xl">🎯</span>
                    </div>
                    <div class="p-4 flex-1 flex flex-col">
                        <div class="flex items-start justify-between">
                            <span class="text-sm font-semibold text-gray-700 truncate w-full">{{ item.short_description
                                ||
                                'Untitled' }}</span>
                            <span class="text-xs text-gray-400 whitespace-nowrap">{{ formatDate(item.created_at)
                                }}</span>
                        </div>
                        <div class="mt-3 text-sm text-gray-600 flex-1 overflow-hidden">
                            <p class="leading-relaxed">{{ truncate(item.description, 80) }}</p>
                        </div>
                        <div class="mt-3 flex items-center justify-between text-xs">
                            <span class="text-gray-500">{{ item.budget_required ? 'Budget required' : 'No budget'
                                }}</span>
                            <button @click.stop="openThinkTank()"
                                class="px-3 py-1 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition">
                                Open
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="isLoading && items.length > 0" class="text-center py-4 text-gray-500">Loading more...</div>
        <div v-if="!hasMore && items.length > 0" class="text-center py-4 text-gray-400">No more ideas</div>
    </div>

</template>

<script>
import gql from 'graphql-tag';

const LIST_USER_THINK_TANKS = gql`
  query ListUserThinkTanks($userToken: String, $first: Int!, $page: Int!) {
    listUserThinkTanks(userToken: $userToken, first: $first, page: $page) {
      error
      error_code
      message
      paginatorInfo { hasMorePages currentPage total }
      likes_total
      comments_total
      data {
        token
        short_description
        description
        budget_required
        estimated_amount
        created_at
        likes_count
        comments_count
      }
    }
  }
`;

export default {
    name: "ThinkTank",
    props: {
        token: {
            type: String,
            default: ""
        },
        userName: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            items: [],
            isLoading: false,
            first: 6,
            page: 1,
            hasMore: true,
            metricIdeas: 0,
            metricLikes: 0,
            metricComments: 0,
        };
    },
    mounted() {
        this.fetchThinkTanks();
        this.$nextTick(() => {
            const el = document.getElementById('dashboard-scroll-container');
            if (el) el.addEventListener('scroll', this.handleScroll);
        });
    },
    beforeUnmount() {
        const el = document.getElementById('dashboard-scroll-container');
        if (el) el.removeEventListener('scroll', this.handleScroll);
    },
    computed: {
        totalLikes() { return this.metricLikes || 0; },
        totalComments() { return this.metricComments || 0; },
    },
    methods: {
        formatDate(val) {
            if (!val) return '';
            try { return new Date(val).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch (e) { return ''; }
        },
        async fetchThinkTanks() {
            if (this.isLoading || !this.hasMore) return;
            this.isLoading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: LIST_USER_THINK_TANKS,
                    variables: { userToken: this.token || null, first: this.first, page: this.page },
                    fetchPolicy: 'network-only'
                });
                const payload = data && data.listUserThinkTanks;
                if (payload && !payload.error) {
                    const list = payload.data || [];
                    if (this.page === 1) this.items = list; else this.items = this.items.concat(list);
                    this.hasMore = payload.paginatorInfo && payload.paginatorInfo.hasMorePages;

                    // metrics from backend
                    this.metricIdeas = (payload.paginatorInfo && payload.paginatorInfo.total) || this.metricIdeas;
                    this.metricLikes = typeof payload.likes_total === 'number' ? payload.likes_total : this.metricLikes;
                    this.metricComments = typeof payload.comments_total === 'number' ? payload.comments_total : this.metricComments;
                }
            } finally {
                this.isLoading = false;
            }
        },
        handleScroll(e) {
            const el = e.target;
            const bottomGap = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (bottomGap < 200 && this.hasMore && !this.isLoading) {
                this.page += 1;
                this.fetchThinkTanks();
            }
        },
        openThinkTank() {
            this.$router.push('/thinktank');
        },
        truncate(text, max) {
            if (!text) return '';
            const str = String(text);
            if (str.length <= max) return str;
            return str.slice(0, max).trimEnd() + '…';
        }
    }
}
</script>

<style lang="scss" scoped></style>
